import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { DeclarationsAuthService } from '../auth/declarations-auth.service';
import { UsersService } from '../core/users.service';
import { FitAndProperDeclaration, FitAndProperDeclarationToAdd } from './fit-and-proper-declaration.model';
import { FitAndProperDeclarationService } from './fit-and-proper-declaration.service';
import { AlertDataPreloadedComponent } from '../shared/alert-data-preloaded.component';
import { FitAndProperDeclarationAckDialogComponent } from './fit-and-proper-declaration-ack-dialog.component';
import { DatePipe, Location } from '@angular/common';
import { SwitchModule } from '@syncfusion/ej2-angular-buttons';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { DialogModule } from '@syncfusion/ej2-angular-popups';

@Component({
  selector: 'ad-fit-and-proper-declaration',
  templateUrl: './fit-and-proper-declaration.component.html',
  imports: [
    AlertDataPreloadedComponent,
    FitAndProperDeclarationAckDialogComponent,
    DatePipe,
    ReactiveFormsModule,
    TextBoxModule,
    DialogModule,
    SwitchModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ['ol > li { margin-bottom: 1rem; }']
})
export class FitAndProperDeclarationComponent implements OnInit {
  public form!: UntypedFormGroup;
  public declarationDate = new Date();
  public employee!: string;
  public corporateTitle!: string;
  public hasLastYearData = false;
  public showAckDialog = false;
  public ackDialogData: any = {};

  public alreadySubmitted = false;

  constructor(
    public authService: DeclarationsAuthService,
    private fb: UntypedFormBuilder,
    private usersService: UsersService,
    private service: FitAndProperDeclarationService,
    private router: Router,
    public location: Location
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      additionalInformation: [
        null,
        RxwebValidators.required({
          conditionalExpression: (x: any) =>
            x.section3Question1 === true ||
            x.section3Question2 === true ||
            x.section3Question3 === true ||
            x.section3Question4 === true ||
            x.section3Question5 === true ||
            x.section3Question6 === true ||
            x.section4Question1 === true ||
            x.section4Question2 === true ||
            x.section4Question3 === true ||
            x.section4Question4 === true ||
            x.section4Question5 === true ||
            x.section4Question6 === true ||
            x.section4Question7 === true ||
            x.section5Question1 === true
        })
      ],
      section2Question1: [false, Validators.requiredTrue],
      section3Question1: [false, Validators.required],
      section3Question2: [false, Validators.required],
      section3Question3: [false, Validators.required],
      section3Question4: [false, Validators.required],
      section3Question5: [false, Validators.required],
      section3Question6: [false, Validators.required],
      section4Question1: [false, Validators.required],
      section4Question2: [false, Validators.required],
      section4Question3: [false, Validators.required],
      section4Question4: [false, Validators.required],
      section4Question5: [false, Validators.required],
      section4Question6: [false, Validators.required],
      section4Question7: [false, Validators.required],
      section5Question1: [false, Validators.required],
      sfcLicenseNumber: [null]
    });

    this.authService.getActiveAccount$().subscribe((account) => (this.employee = account?.name ?? ''));

    this.usersService.getCurrentUserCorporateTitle$().subscribe((title) => (this.corporateTitle = title));

    this.service.getMine$().subscribe((item) => {
      this.alreadySubmitted = !!item;

      if (this.alreadySubmitted && item) {
        this.populateInitialData(item, false);
        this.form.disable();
      } else {
        this.service.getMineFromLastYear$().subscribe((lastYearData) => {
          if (lastYearData) {
            this.populateInitialData(lastYearData, true);
          }
        });
      }
    });
  }

  public submitForm(): void {
    this.ackDialogData = {
      name: this.employee,
      declarationDate: this.declarationDate
    };
    this.showAckDialog = true;
  }

  public handleDialogClose(data: any): void {
    if (data && data.name) {
      const value: FitAndProperDeclarationToAdd = {
        ...this.form.value,
        corporateTitle: this.corporateTitle,
        employee: data.name,
        declarationDate: data.declarationDate || this.ackDialogData.declarationDate
      };

      this.form.reset();
      this.form.disable();

      this.service.acknowledge$(value).subscribe(() => this.router.navigate(['/']));
    }
    this.showAckDialog = false;
  }

  private populateInitialData(data: FitAndProperDeclaration, isLastYearData: boolean): void {
    if (data) {
      if (isLastYearData) {
        this.hasLastYearData = isLastYearData;
        this.form.get('sfcLicenseNumber')?.setValue(data.sfcLicenseNumber);
      } else {
        this.form.get('additionalInformation')?.setValue(data.additionalInformation);
        this.form.get('section2Question1')?.setValue(data.section2Question1);
        this.form.get('section3Question1')?.setValue(data.section3Question1);
        this.form.get('section3Question2')?.setValue(data.section3Question2);
        this.form.get('section3Question3')?.setValue(data.section3Question3);
        this.form.get('section3Question4')?.setValue(data.section3Question4);
        this.form.get('section3Question5')?.setValue(data.section3Question5);
        this.form.get('section3Question6')?.setValue(data.section3Question6);
        this.form.get('section4Question1')?.setValue(data.section4Question1);
        this.form.get('section4Question2')?.setValue(data.section4Question2);
        this.form.get('section4Question3')?.setValue(data.section4Question3);
        this.form.get('section4Question4')?.setValue(data.section4Question4);
        this.form.get('section4Question5')?.setValue(data.section4Question5);
        this.form.get('section4Question6')?.setValue(data.section4Question6);
        this.form.get('section4Question7')?.setValue(data.section4Question7);
        this.form.get('section5Question1')?.setValue(data.section5Question1);
        this.form.get('sfcLicenseNumber')?.setValue(data.sfcLicenseNumber);

        this.declarationDate = data.declarationDate;
        this.employee = data.employee;
      }
    }
  }
}
